/**
 * Account Cancel Membership Page
 * Cancel subscription with feedback
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, Calendar, AlertTriangle, Loader2 } from 'lucide-react';
import AccountHeader from '../../components/account/AccountHeader';
import { useAccountAuth } from '../../contexts/AccountAuthContext';
import { API_BASE_URL } from '../../config/api';

const CANCELLATION_REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'not_using', label: 'Not using it enough' },
  { id: 'missing_features', label: 'Missing features I need' },
  { id: 'competitor', label: 'Switching to a competitor' },
  { id: 'technical_issues', label: 'Technical issues' },
  { id: 'other', label: 'Other' }
];

const AccountCancel = () => {
  const navigate = useNavigate();
  const { subscription } = useAccountAuth();
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Get features user will lose based on current plan
  const getFeaturesToLose = () => {
    const items = [];
    if (subscription?.maxInventoryUnits) items.push(`Access to ${subscription.maxInventoryUnits} property listings`);
    if (subscription?.maxLocations) items.push(`Up to ${subscription.maxLocations} business locations`);
    if (subscription?.features?.aiConversationsMonthly) items.push(`${subscription.features.aiConversationsMonthly} AI conversations per month`);
    if (subscription?.features?.aiMemoryEnabled) items.push('AI memory for personalized responses');
    if (subscription?.features?.featuredListings) items.push(`${subscription.features.featuredListings} featured listings`);
    if (subscription?.features?.prioritySupport) items.push('Priority support');
    if (subscription?.features?.voiceListingEnabled) items.push('Voice listing creation');
    if (subscription?.features?.lateFeeAutomation) items.push('Late fee automation');
    if (subscription?.features?.teamMembersLimit > 1) items.push(`Up to ${subscription.features.teamMembersLimit} team members`);
    if (items.length === 0) items.push('Access to your current plan features');
    return items;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleCancel = async () => {
    setIsLoading(true);
    try {
      const email = sessionStorage.getItem('account_email') || '';
      const pwd = sessionStorage.getItem('account_pwd') || '';

      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'cancel_sub',
          email,
          pwd,
          reason: selectedReason || undefined
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Notify the native app (WebView) that subscription was cancelled
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'subscription_cancelled' }));
        } else {
          // Fallback for browser (non-WebView) usage
          alert('Your subscription has been cancelled successfully.');
          navigate('/app/account/dashboard');
        }
      } else {
        alert(result.message || 'Failed to cancel subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      <AccountHeader title="Cancel Membership" />
      
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Sad Face Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-6"
        >
          <div className="text-5xl mb-4">😢</div>
          <h2 className="text-2xl font-bold text-white">We're sorry to see you go</h2>
        </motion.div>

        {/* What You'll Lose */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5"
        >
          <h3 className="text-sm font-medium text-red-400 mb-4">WHAT YOU'LL LOSE</h3>
          <ul className="space-y-2.5">
            {getFeaturesToLose().map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-gray-300">
                <X size={16} className="text-red-400 shrink-0" />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Access End Date */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-dark-100 border border-white/10 rounded-2xl p-5"
        >
          <h3 className="text-sm font-medium text-gray-400 mb-4">YOUR ACCESS ENDS</h3>
          <p className="text-gray-400 text-sm mb-3">
            Your {subscription?.planName} will remain active until:
          </p>
          <div className="flex items-center gap-3 p-4 bg-dark-200 rounded-xl">
            <Calendar className="w-6 h-6 text-accent" />
            <span className="text-xl font-semibold text-white">
              {formatDate(subscription?.currentPeriodEnd)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            After this date, your account will be downgraded to the free tier with limited features.
          </p>
        </motion.div>

        {/* Feedback Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-dark-100 border border-white/10 rounded-2xl p-5"
        >
          <h3 className="text-sm font-medium text-gray-400 mb-4">BEFORE YOU GO...</h3>
          <p className="text-sm text-gray-300 mb-4">Tell us why you're leaving (optional)</p>
          
          {/* Reason Options */}
          <div className="space-y-2 mb-4">
            {CANCELLATION_REASONS.map((reason) => (
              <label
                key={reason.id}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                  selectedReason === reason.id
                    ? 'bg-accent/10 border border-accent/30'
                    : 'bg-dark-200 border border-transparent hover:border-white/10'
                }`}
              >
                <input
                  type="radio"
                  name="reason"
                  value={reason.id}
                  checked={selectedReason === reason.id}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-4 h-4 text-accent border-white/20 bg-dark-300 focus:ring-accent"
                />
                <span className="text-sm text-gray-300">{reason.label}</span>
              </label>
            ))}
          </div>

          {/* Additional Feedback */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Additional feedback (optional)</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell us more about your experience..."
              rows={3}
              className="w-full px-4 py-3 bg-dark-200 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent transition-colors resize-none"
            />
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-3 pb-6"
        >
          <button
            onClick={() => navigate('/app/account/dashboard')}
            className="w-full py-4 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all"
          >
            Keep My Subscription
          </button>
          
          <button
            onClick={() => setShowConfirm(true)}
            className="w-full py-4 border border-red-500/50 text-red-400 font-semibold rounded-xl hover:bg-red-500/10 transition-all"
          >
            Cancel Membership
          </button>
        </motion.div>
      </div>

      {/* Final Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-dark-100 rounded-2xl p-6 border border-white/10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white">Confirm Cancellation</h3>
            </div>
            
            <p className="text-gray-400 mb-6">
              Are you sure you want to cancel your subscription? You'll lose access to all premium features at the end of your current billing period.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={isLoading}
                className="flex-1 py-3 border border-white/20 text-white rounded-xl hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                Go Back
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="flex-1 py-3 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AccountCancel;
